import React, { useState, useEffect, useRef, useCallback } from 'react';
import './PromptConfig.css';
import { getPrompts, createPrompt, updatePrompt } from '../Services/PromptService';
import { useMsal } from '@azure/msal-react';

type Prompt = {
  id?: number;
  promptType: string;
  promptName: string;
  promptText: string;
};

const SYSTEM_PROMPT_DEFAULT = '';
const KILL_MEMO_DEFAULT = '';
const CULTIVATION_MEMO_DEFAULT = '';

const PromptConfig: React.FC = () => {
  const { instance, accounts } = useMsal();

  // System prompt state
  const [systemPrompt, setSystemPrompt] = useState<Prompt>({
    promptType: 'System',
    promptName: 'System',
    promptText: SYSTEM_PROMPT_DEFAULT,
  });

  // User prompts state
  const [killMemoPrompt, setKillMemoPrompt] = useState<Prompt>({
    promptType: 'User',
    promptName: 'Kill Memo',
    promptText: KILL_MEMO_DEFAULT,
  });
  const [cultivationMemoPrompt, setCultivationMemoPrompt] = useState<Prompt>({
    promptType: 'User',
    promptName: 'Cultivation memo',
    promptText: CULTIVATION_MEMO_DEFAULT,
  });
  const [additionalPrompts, setAdditionalPrompts] = useState<Prompt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: number]: string }>({});
  const [systemPromptError, setSystemPromptError] = useState<string>('');
  const [killMemoError, setKillMemoError] = useState<string>('');
  const [cultivationMemoError, setCultivationMemoError] = useState<string>('');

  const didFetch = useRef(false);

  // Helper function to count words
  const countWords = useCallback((text: string) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  }, []);

  // Fetch prompts on mount
  useEffect(() => {
    if (!accounts || accounts.length === 0 || didFetch.current) return;
    didFetch.current = true;
    const fetchPrompts = async () => {
      try {
        const data: Prompt[] = await getPrompts(instance, accounts[0]);
        // System prompt
        const sys = data.find(p => p.promptType === 'System');
        if (sys) setSystemPrompt(sys);

        // Kill Memo
        const kill = data.find(p => p.promptType === 'User' && p.promptName === 'Kill Memo');
        if (kill) setKillMemoPrompt(kill);

        // Cultivation Memo
        const cult = data.find(p => p.promptType === 'User' && p.promptName === 'Cultivation memo');
        if (cult) setCultivationMemoPrompt(cult);

        // Additional user prompts
        const additional = data.filter(
          p =>
            p.promptType === 'User' &&
            p.promptName !== 'Kill Memo' &&
            p.promptName !== 'Cultivation memo'
        );
        setAdditionalPrompts(additional);
        
        // Validate fixed prompts on load
        setSystemPromptError(validateFixedPrompt(sys?.promptText || ''));
        setKillMemoError(validateFixedPrompt(kill?.promptText || ''));
        setCultivationMemoError(validateFixedPrompt(cult?.promptText || ''));
      } catch {
        setError('Failed to load prompts');
      }
    };
    fetchPrompts();
    // eslint-disable-next-line
  }, [instance, accounts]);

  // Validation function for prompt names and content
  const validatePrompts = useCallback(() => {
    const errors: { [key: number]: string } = {};
    const allPromptNames = new Set<string>();
    const MAX_PROMPT_WORDS = 8000;
    
    // Add fixed prompt names
    allPromptNames.add('Kill Memo');
    allPromptNames.add('Cultivation memo');
    
    // Check additional prompts for validation
    additionalPrompts.forEach((prompt, idx) => {
      const trimmedName = prompt.promptName.trim();
      const trimmedText = prompt.promptText.trim();
      const wordCount = countWords(trimmedText);
      
      // Check prompt text word count
      if (wordCount > MAX_PROMPT_WORDS) {
        errors[idx] = `Prompt text must not exceed ${MAX_PROMPT_WORDS} words (current: ${wordCount})`;
      }
      // Check if prompt has content but no name
      else if (trimmedText && !trimmedName) {
        errors[idx] = 'Prompt name is required';
      }
      // Check if prompt has name but no content
      else if (trimmedName && !trimmedText) {
        errors[idx] = 'Prompt text is required';
      }
      // Check for duplicate names (only if name is not empty)
      else if (trimmedName) {
        if (allPromptNames.has(trimmedName)) {
          errors[idx] = 'Prompt name must be unique';
        } else {
          allPromptNames.add(trimmedName);
        }
      }
    });
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [additionalPrompts, countWords]);

  // Validation function for fixed prompts
  const validateFixedPrompt = useCallback((text: string) => {
    const MAX_PROMPT_WORDS = 8000;
    const trimmedText = text.trim();
    const wordCount = countWords(trimmedText);
    
    if (wordCount > MAX_PROMPT_WORDS) {
      return `Prompt text must not exceed ${MAX_PROMPT_WORDS} words (current: ${wordCount})`;
    }
    return '';
  }, [countWords]);

  // Run validation when additional prompts change
  useEffect(() => {
    if (additionalPrompts.length > 0) {
      validatePrompts();
    } else {
      setValidationErrors({});
    }
  }, [additionalPrompts, validatePrompts]);

  // Handlers for system and fixed user prompts
  const handleSystemPromptChange = (text: string) => {
    setSystemPrompt(prev => ({ ...prev, promptText: text }));
    setSystemPromptError(validateFixedPrompt(text));
  };
  const handleKillMemoChange = (text: string) => {
    setKillMemoPrompt(prev => ({ ...prev, promptText: text }));
    setKillMemoError(validateFixedPrompt(text));
  };
  const handleCultivationMemoChange = (text: string) => {
    setCultivationMemoPrompt(prev => ({ ...prev, promptText: text }));
    setCultivationMemoError(validateFixedPrompt(text));
  };

  // Handlers for additional prompts
  const handleAdditionalPromptChange = (idx: number, field: 'promptName' | 'promptText', value: string) => {
    setAdditionalPrompts(prompts => {
      const newPrompts = [...prompts];
      newPrompts[idx] = { ...newPrompts[idx], [field]: value };
      return newPrompts;
    });
    
    // Trigger validation when prompt name or text changes
    setTimeout(() => validatePrompts(), 0);
  };

  const handleAddPrompt = () => {
    setAdditionalPrompts(prompts => [
      ...prompts,
      { promptType: 'User', promptName: '', promptText: '' }
    ]);
    // Clear validation errors when adding a new prompt
    setValidationErrors({});
  };

  // Save all prompts
  const handleSaveAll = async () => {
    setError(null);
    
    // Validate prompts before saving
    if (!validatePrompts()) {
      setError('Please fix validation errors before saving');
      return;
    }
    
    // Check for fixed prompt validation errors
    if (systemPromptError || killMemoError || cultivationMemoError) {
      setError('Please fix validation errors before saving');
      return;
    }
    
    setIsLoading(true);
    try {
      // Save system prompt
      if (systemPrompt.id) {
        await updatePrompt(instance, systemPrompt.id, systemPrompt, accounts[0]);
      } else if (systemPrompt.promptText) {
        const created = await createPrompt(instance, { ...systemPrompt }, accounts[0]);
        setSystemPrompt(prev => ({ ...prev, id: created.id }));
      }

      // Save kill memo
      if (killMemoPrompt.id) {
        await updatePrompt(instance, killMemoPrompt.id, killMemoPrompt, accounts[0]);
      } else if (killMemoPrompt.promptText) {
        const created = await createPrompt(instance, { ...killMemoPrompt }, accounts[0]);
        setKillMemoPrompt(prev => ({ ...prev, id: created.id }));
      }

      // Save cultivation memo
      if (cultivationMemoPrompt.id) {
        await updatePrompt(instance, cultivationMemoPrompt.id, cultivationMemoPrompt, accounts[0]);
      } else if (cultivationMemoPrompt.promptText) {
        const created = await createPrompt(instance, { ...cultivationMemoPrompt }, accounts[0]);
        setCultivationMemoPrompt(prev => ({ ...prev, id: created.id }));
      }

      // Save additional prompts
      const updatedAdditionalPrompts = await Promise.all(
        additionalPrompts.map(async (prompt) => {
          const trimmedName = prompt.promptName.trim();
          const trimmedText = prompt.promptText.trim();
          
          if (prompt.id) {
            await updatePrompt(instance, prompt.id, prompt, accounts[0]);
            return prompt;
          } else if (trimmedName && trimmedText) {
            const created = await createPrompt(instance, prompt, accounts[0]);
            return { ...prompt, id: created.id };
          }
          return prompt;
        })
      );
      setAdditionalPrompts(updatedAdditionalPrompts);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000); // Hide success message after 3 seconds
    } catch {
      setError('Failed to save prompts');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="prompt-config-container prompt-config-outer">
      {isLoading && (
        <div className="spinner-overlay">
          <div className="spinner spinner-large" />
        </div>
      )}
      {/* System Prompt Section */}
      <div className="prompt-section">
        <div className="prompt-label-row">
          <span className="prompt-label">System Prompt</span>
          <span className="prompt-word-count">
            {countWords(systemPrompt.promptText)}/8000 words
          </span>
        </div>
        <textarea
          className={`prompt-textarea prompt-system-textarea ${systemPromptError ? 'prompt-textarea-error' : ''}`}
          value={systemPrompt.promptText}
          onChange={e => handleSystemPromptChange(e.target.value)}
          placeholder="Enter system prompt here..."
        />
        {systemPromptError && (
          <div className="prompt-validation-error">{systemPromptError}</div>
        )}
      </div>

      {/* Kill Memo Prompt */}
      <div className="prompt-section">
        <div className="prompt-label-row">
          <span className="prompt-label">User Prompt</span>
        </div>
        <div className="prompt-user-block">
          <div className="prompt-name-row">
            <span className="prompt-name-label">Prompt name</span>
            <input
              className="prompt-name-input prompt-name-input-fixed"
              type="text"
              value={killMemoPrompt.promptName}
              readOnly
              disabled
            />
          </div>
          <textarea
            className={`prompt-textarea ${killMemoError ? 'prompt-textarea-error' : ''}`}
            value={killMemoPrompt.promptText}
            onChange={e => handleKillMemoChange(e.target.value)}
            placeholder="Enter Kill Memo prompt..."
          />
          <div className="prompt-word-count">
            {countWords(killMemoPrompt.promptText)}/8000 words
          </div>
          {killMemoError && (
            <div className="prompt-validation-error">{killMemoError}</div>
          )}
        </div>
        {/* Cultivation Memo Prompt */}
        <div className="prompt-user-block">
          <div className="prompt-name-row">
            <span className="prompt-name-label">Prompt name</span>
            <input
              className="prompt-name-input prompt-name-input-fixed"
              type="text"
              value={cultivationMemoPrompt.promptName}
              readOnly
              disabled
            />
          </div>
          <textarea
            className={`prompt-textarea ${cultivationMemoError ? 'prompt-textarea-error' : ''}`}
            value={cultivationMemoPrompt.promptText}
            onChange={e => handleCultivationMemoChange(e.target.value)}
            placeholder="Enter Cultivation memo prompt..."
          />
          <div className="prompt-word-count">
            {countWords(cultivationMemoPrompt.promptText)}/8000 words
          </div>
          {cultivationMemoError && (
            <div className="prompt-validation-error">{cultivationMemoError}</div>
          )}
        </div>
        {/* Additional User Prompts */}
        {additionalPrompts.map((prompt, idx) => (
          <div key={prompt.id || idx} className="prompt-user-block">
            <div className="prompt-name-row">
              <span className="prompt-name-label">Prompt name</span>
              <input
                className={`prompt-name-input ${validationErrors[idx] ? 'prompt-name-input-error' : ''}`}
                type="text"
                value={prompt.promptName}
                onChange={e => handleAdditionalPromptChange(idx, 'promptName', e.target.value)}
              />
            </div>
            {validationErrors[idx] && (
              <div className="prompt-validation-error">{validationErrors[idx]}</div>
            )}
            <textarea
              className={`prompt-textarea ${validationErrors[idx] && (validationErrors[idx].includes('text') || validationErrors[idx].includes('words')) ? 'prompt-textarea-error' : ''}`}
              value={prompt.promptText}
              onChange={e => handleAdditionalPromptChange(idx, 'promptText', e.target.value)}
              placeholder="Enter prompt..."
            />
            <div className="prompt-word-count">
              {countWords(prompt.promptText)}/8000 words
            </div>
          </div>
        ))}
        <div className="prompt-action-row">
          <button
            className="prompt-add-btn"
            onClick={handleAddPrompt}
            disabled={isLoading}
          >
            Add prompt
          </button>
          <button
            className="prompt-save-btn"
            onClick={handleSaveAll}
            disabled={isLoading}
          >
            Save
          </button>
        </div>
        {error && <div className="prompt-error">{error}</div>}
        {showSuccess && (
          <div className="success-popup">
            Prompts saved successfully!
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptConfig;
